import { EUserRole } from '@/types';

/**
 * Role sets mirroring the backend `@PreAuthorize(...)` lists that guard Safety
 * and Environment **write** endpoints.
 *
 * Those two modules authorize mutations by ROLE, not by the `safety.*` /
 * `environment.*` permission keys, and the seeded role→permission defaults do
 * not line up with the decorators — the Mine Manager may write environmental
 * records without holding `environment.create`, and the Mine Director may log a
 * hazard without holding `safety.create`. Gating those controls with `can()`
 * therefore hides buttons the API would happily accept, while leaving them
 * ungated shows buttons that 403 on submit. These lists are the honest mirror
 * of what the server enforces today.
 *
 * Source of truth: `safety.controller.ts` and `environment.controller.ts`.
 * Keep in lock-step whenever those decorators change.
 */

/** `POST /safety/hazards` — log a hazard observation. */
export const HAZARD_LOG_ROLES: EUserRole[] = [
  EUserRole.SENIOR_SAFETY_ENGINEER,
  EUserRole.JUNIOR_SAFETY_ENGINEER,
  EUserRole.FIELD_SUPERVISOR,
  EUserRole.MINE_MANAGER,
  EUserRole.MINE_DIRECTOR,
];

/**
 * `POST /safety/corrective-actions` — raise a corrective action. Excludes the
 * Mine Director, whose safety scope is read / export / approve (SRS §4.2.3).
 */
export const CORRECTIVE_ACTION_CREATE_ROLES: EUserRole[] = [
  EUserRole.SENIOR_SAFETY_ENGINEER,
  EUserRole.JUNIOR_SAFETY_ENGINEER,
  EUserRole.MINE_MANAGER,
];

/** `POST /safety/hazards/:id/triage` — promote or close an observation. */
export const HAZARD_TRIAGE_ROLES: EUserRole[] = [
  EUserRole.SENIOR_SAFETY_ENGINEER,
  EUserRole.MINE_MANAGER,
];

/**
 * `INCIDENT_REVIEW_ROLES` on the backend — submitting inspections, creating
 * inspection schedules, and reviewing incident state.
 */
export const SAFETY_REVIEW_ROLES: EUserRole[] = [
  EUserRole.SENIOR_SAFETY_ENGINEER,
  EUserRole.JUNIOR_SAFETY_ENGINEER,
  EUserRole.MINE_MANAGER,
  EUserRole.MINE_DIRECTOR,
  EUserRole.ENVIRONMENTALIST,
];

/**
 * Every `POST` / `PATCH` under `/environment` — monitoring records, parameters,
 * environmental incidents, and compliance evidence. Deliberately excludes the
 * Government Inspector, who can read all of it but must not mutate.
 */
export const ENVIRONMENT_WRITE_ROLES: EUserRole[] = [
  EUserRole.ENVIRONMENTALIST,
  EUserRole.MINE_MANAGER,
  EUserRole.MINE_DIRECTOR,
];

/**
 * FR-TRACE-023 — `GET /operations/{meus,lots}/:id/certificate`. The traceability
 * certificate is an export/compliance document, so field roles that can view the
 * MEU itself still cannot mint one.
 */
export const TRACEABILITY_CERTIFICATE_ROLES: EUserRole[] = [
  EUserRole.MINE_DIRECTOR,
  EUserRole.MINE_MANAGER,
  EUserRole.FINANCE_OFFICER,
  EUserRole.GOVERNMENT_INSPECTOR,
  EUserRole.SUPER_ADMIN,
];

/**
 * FR-DEV-001 — `POST /operations/development-plans`. Field Supervisor was
 * seeing the "Create Plan" button on the Development Tracker with no gate,
 * so clicking it always 403'd — the plan is a manager/engineer-level
 * commitment, not something logged from the field.
 */
export const DEVELOPMENT_PLAN_CREATE_ROLES: EUserRole[] = [
  EUserRole.SUPER_ADMIN,
  EUserRole.MINE_DIRECTOR,
  EUserRole.MINE_MANAGER,
  EUserRole.MINE_ENGINEER,
];

/**
 * FR-OPS-010/013/015 — `POST /operations/production-logs`,
 * `.../:id/submit`, `.../submit-batch`. All three carry the identical
 * `@PreAuthorize` list on the backend, Field Supervisor included — they're
 * the one filing the shift's production log in the first place. The
 * frontend was gating New Log / Submit behind `operations.edit` /
 * `operations.create` permissions instead, and Field Supervisor's seeded
 * defaults don't hold those, so every submit control silently disappeared
 * for the role that needs it most (SRS role-vs-permission mismatch, same
 * shape as the Safety/Environment lists above).
 */
export const PRODUCTION_LOG_WRITE_ROLES: EUserRole[] = [
  EUserRole.SUPER_ADMIN,
  EUserRole.MINE_DIRECTOR,
  EUserRole.MINE_MANAGER,
  EUserRole.MINE_ENGINEER,
  EUserRole.FIELD_SUPERVISOR,
];

/**
 * `GET /operations/meus` — backs the MEU picker in the Create Lot dialog, so
 * this is really "who can create a lot" even though `POST /operations/lots`
 * itself is only coarsely gated. Government Inspector isn't in the `meus`
 * list (read-only oversight role, no operational MEU access) — Lot
 * Management was calling `useAvailableMeus()` unconditionally on page load
 * regardless of role, so every Inspector visit 403'd immediately.
 */
export const LOT_CREATE_ROLES: EUserRole[] = [
  EUserRole.SUPER_ADMIN,
  EUserRole.MINE_DIRECTOR,
  EUserRole.MINE_MANAGER,
  EUserRole.MINE_ENGINEER,
  EUserRole.FIELD_SUPERVISOR,
  EUserRole.STOCK_MANAGER,
];

/**
 * `POST /operations/lots/:id/split`, `.../merge`. Mirrors the backend's
 * `@RequirePermissions(operations.create, operations.edit)` (ANY-of) added
 * alongside this gate — Stock Manager only holds `operations.view` in the
 * seeded defaults and Government Inspector holds neither, so both would
 * 403 despite `LOT_CREATE_ROLES` (matched to the coarser `GET /meus` list)
 * showing them the button. Splitting/merging mutates chain-of-custody data,
 * unlike creating a lot, so this list is intentionally narrower.
 */
export const LOT_SPLIT_MERGE_ROLES: EUserRole[] = [
  EUserRole.SUPER_ADMIN,
  EUserRole.MINE_DIRECTOR,
  EUserRole.MINE_MANAGER,
  EUserRole.MINE_ENGINEER,
  EUserRole.FIELD_SUPERVISOR,
];

/**
 * FR-TRACE-003 — `POST /operations/meus`. Registering an MEU is the
 * "confirm an extraction" act, hardcoded in `operations.service.ts`'s
 * `createMeu` as `allowedConfirmerRoles` rather than a `@RequirePermissions`
 * check. Stock Manager has route access to Bag Tracking / MEU (read, plus
 * custody-transfer and lot write access elsewhere in traceability) but was
 * seeing an ungated "Register MEU" button that always 403'd — confirming an
 * extraction is a field/engineering/management responsibility, not stock's.
 */
export const MEU_CREATE_ROLES: EUserRole[] = [
  EUserRole.SUPER_ADMIN,
  EUserRole.MINE_DIRECTOR,
  EUserRole.MINE_MANAGER,
  EUserRole.MINE_ENGINEER,
  EUserRole.FIELD_SUPERVISOR,
];

/**
 * FR-BLAST-004 — `POST /operations/blaster-certificates`, `PATCH .../:id`,
 * `POST .../:id/revoke`. All three consistently exclude Mine Engineer and
 * Sr. Safety Engineer on the backend — they're nav-granted read access to
 * the registry (to verify a blaster's certification status) but not write
 * access. The frontend's Register/Edit/Revoke controls were completely
 * ungated, so both roles saw and could click actions that always 403'd.
 */
export const BLASTER_CERTIFICATE_MANAGE_ROLES: EUserRole[] = [
  EUserRole.SUPER_ADMIN,
  EUserRole.MINE_DIRECTOR,
  EUserRole.MINE_MANAGER,
  EUserRole.IT_ADMINISTRATOR,
];

/**
 * `GET /operations/analytics/operational-efficiency` (SRS §13.3.2). Backs the
 * shift-performance KPI/chart on the Shifts page. IT Administrator has
 * `shift.view` and can reach that page, but this analytics endpoint's own
 * `@PreAuthorize` doesn't include it — calling it unconditionally 403'd the
 * whole page load for that role. Field Supervisor is explicitly included on
 * both the backend decorator and here.
 */
export const SHIFT_PERFORMANCE_VIEW_ROLES: EUserRole[] = [
  EUserRole.SUPER_ADMIN,
  EUserRole.MINE_DIRECTOR,
  EUserRole.MINE_MANAGER,
  EUserRole.MINE_ENGINEER,
  EUserRole.FIELD_SUPERVISOR,
];

// ---------------------------------------------------------------------------
// Appendix B.4 modules
//
// Three endpoints in these modules grant the permission more widely than the
// `@PreAuthorize` list that guards them, so `can()` alone is too permissive and
// the control 403s on submit. Found by driving the real API per role rather
// than by reading the code — the decorators and the seeded grants disagree, and
// nothing in either repository's type system can see it.
// ---------------------------------------------------------------------------

/**
 * `POST /surveying/coordinate-system` — set the tenant's projection.
 *
 * `surveying.approve` is also held by the Mine Engineer and Mine Manager so
 * they can review reconciliations, but the coordinate system is a tenant-wide
 * setting and its decorator is narrower.
 */
export const COORDINATE_SYSTEM_SET_ROLES: EUserRole[] = [
  EUserRole.SUPER_ADMIN,
  EUserRole.MINE_DIRECTOR,
  EUserRole.IT_ADMINISTRATOR,
];

/**
 * `POST /surveying/reconciliations/:id/review` — sign off a flagged variance.
 * The IT Administrator holds `surveying.approve` but this is an operational
 * judgement, so the decorator leaves that role out.
 */
export const RECONCILIATION_REVIEW_ROLES: EUserRole[] = [
  EUserRole.SUPER_ADMIN,
  EUserRole.MINE_DIRECTOR,
  EUserRole.MINE_MANAGER,
  EUserRole.MINE_ENGINEER,
];

/**
 * `POST /procurement/approval-thresholds` — define who must approve an order at
 * what value. The Mine Manager holds `procurement.approve` for approving orders,
 * but setting the bands decides their own signing limit, so the decorator keeps
 * that to the Director.
 */
export const APPROVAL_BAND_SET_ROLES: EUserRole[] = [
  EUserRole.SUPER_ADMIN,
  EUserRole.MINE_DIRECTOR,
];
